import { useEffect, useRef } from "react";

export interface BusNotification {
    type: string;
    payload: Record<string, unknown>;
}

type Handler = (notification: BusNotification) => void;

const WS_VERSION = "19.0-2";
const MAX_BACKOFF = 30_000;

/**
 * The live half of the discussion system.
 *
 * Odoo already pushes every channel the signed-in partner belongs to on
 * `/websocket`, so the client subscribes with an empty channel list and
 * receives `discuss.channel/new_message`, `simple_notification` and the rest.
 * What arrives is a signal, never the data of record: callers refetch through
 * the REST API, which stays the single way data is read.
 */
export function useBusSocket(handler: Handler, enabled = true) {
    const latest = useRef(handler);
    latest.current = handler;

    useEffect(() => {
        if (!enabled || typeof window === "undefined" || !("WebSocket" in window)) {
            return;
        }
        let socket: WebSocket | null = null;
        let stopped = false;
        let attempt = 0;
        let retry: number | undefined;

        function connect() {
            const scheme = window.location.protocol === "https:" ? "wss" : "ws";
            socket = new WebSocket(
                `${scheme}://${window.location.host}/websocket?version=${WS_VERSION}`);
            socket.onopen = () => {
                attempt = 0;
                socket?.send(JSON.stringify({
                    event_name: "subscribe",
                    data: { channels: [], last: 0 },
                }));
            };
            socket.onmessage = (event) => {
                let frames: { message?: { type?: string; payload?: Record<string, unknown> } }[];
                try {
                    frames = JSON.parse(event.data);
                } catch {
                    return;
                }
                for (const frame of frames) {
                    const type = frame.message?.type;
                    if (type) {
                        latest.current({ type, payload: frame.message?.payload ?? {} });
                    }
                }
            };
            socket.onclose = () => {
                if (stopped) {
                    return;
                }
                attempt += 1;
                retry = window.setTimeout(
                    connect, Math.min(MAX_BACKOFF, 1000 * 2 ** Math.min(attempt, 5)));
            };
        }

        connect();
        return () => {
            stopped = true;
            window.clearTimeout(retry);
            socket?.close();
        };
    }, [enabled]);
}
